import { RealtimeMessage, CreateWebsocketClientRequest } from '@inplay/models';

export type MessageHandler = (data: RealtimeMessage) => void;
export type EventHandler = () => void;

export class WebSocketService {
  private socket: WebSocket | null = null;
  private url: string;

  private onOpenHandler?: EventHandler;
  private onCloseHandler?: EventHandler;

  private onMessageHandlers: Set<MessageHandler> = new Set();

  private heartbeatTimer: number | null = null;
  private heartbeatIntervalMs = 12000;

  constructor(url: string, heartbeatInterval?: number) {
    this.url = url;
    this.heartbeatIntervalMs = heartbeatInterval || this.heartbeatIntervalMs;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const messageFactory = () =>
      ({
        type: 'Heartbeat',
        ts: Date.now(),
      }) as unknown as CreateWebsocketClientRequest;

    this.heartbeatTimer = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        try {
          const payload: CreateWebsocketClientRequest = messageFactory();
          this.sendMessage(payload);
        } catch (err) {
          console.warn(
            '[WebSocketService][heartbeat] Failed to send heartbeat',
            err,
          );
        }
      }
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
      console.log('[WebSocketService][stopHeartbeat]');
    }
  }

  public connect(): void {
    console.log('[WebSocketService][connect][ENTRY]', { url: this.url });
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      console.warn('[WebSocketService][connect] already connected.');
      return;
    }

    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      console.log('[WebSocketService][connect] opened');
      this.startHeartbeat();
      this.onOpenHandler?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const eventMessage = JSON.parse(event.data);
        console.log('[WebSocketService][onMessage][ENTRY]', {
          event,
          eventMessageType: eventMessage.type,
        });

        if (eventMessage?.type === 'Heartbeat') return;

        this.onMessageHandlers.forEach((handler) => handler(eventMessage));
      } catch (error) {
        console.error('Failed to parse message:', event.data);
      }
    };

    this.socket.onclose = () => {
      console.log('[WebSocketService][close] closed — reconnecting in 3s');
      this.stopHeartbeat();
      this.onCloseHandler?.();
      setTimeout(() => this.connect(), 3000);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  public sendMessage(message: CreateWebsocketClientRequest): void {
    console.log('[WebSocketService][sendMessage][ENTRY]', { message });
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocketService][sendMessage] socket not open');
    }
  }

  public onOpen(handler: EventHandler): void {
    this.onOpenHandler = handler;
  }

  public onMessage(handler: MessageHandler): () => void {
    console.log('[WebSocketService][onMessage][ENTRY]', {
      handler,
      onMessageHandlersCount: this.onMessageHandlers.size,
    });

    this.onMessageHandlers.add(handler);

    // return unsubscribe function for cleanup
    return () => {
      console.log('[WebsocketService][onMessage] unsubscribe cleanup');
      this.onMessageHandlers.delete(handler);
      console.log('[WebsocketService][onMessage] unsubscribe cleanup finished');
    };
  }

  public onClose(handler: EventHandler): void {
    console.log('[WebSocketService][onClose][ENTRY]', {
      onCloseHandlerCount: this.onCloseHandler?.toString().length,
    });
    this.onCloseHandler = handler;
    console.log('[WebSocketService][onClose][EXIT]', {
      onCloseHandlerCount: this.onCloseHandler?.toString().length,
    });
  }

  public disconnect(): void {
    console.log('[WebSocketService][disconnect]');
    this.stopHeartbeat();
    this.socket?.close();
    console.log('[WebSocketService][disconnect] finished');
  }
}
